import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Bot, Check, X, Loader2, Clock, ShieldAlert, MessageCircle } from "lucide-react";
import Header from "@/components/Header";
import { getSessionToken } from "@/utils/auth";

const API = `${window.APP_CONFIG?.BACKEND_URL || process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${getSessionToken()}` } });
const when = (s) => (s ? new Date(s).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZone: "America/Los_Angeles" }) + " PT" : "");

const TABS = [
  { id: "pending", label: "Waiting" },
  { id: "scheduled", label: "Held" },
  { id: "sent,failed,blocked", label: "Sent / Failed" },
  { id: "rejected", label: "Rejected" },
];

const STATUS_STYLE = {
  pending: "bg-amber-100 text-amber-800", scheduled: "bg-blue-100 text-blue-800", sent: "bg-green-100 text-green-800",
  failed: "bg-red-100 text-red-700", blocked: "bg-red-100 text-red-700", rejected: "bg-zinc-200 text-zinc-600",
  approved: "bg-green-100 text-green-800", sending: "bg-blue-100 text-blue-800",
};

function DraftCard({ d, selected, onSelect, onApprove, onReject, busy }) {
  const [body, setBody] = useState(d.body);
  const c = d.customer || {};
  const pending = d.status === "pending";
  return (
    <div className="bg-white rounded-xl border p-4 space-y-3" data-testid={`ai-draft-${d.draft_id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-2 min-w-0">
          {pending && (
            <input type="checkbox" className="mt-1" checked={selected} onChange={() => onSelect(d.draft_id)}
              disabled={c.opted_out} data-testid={`ai-draft-select-${d.draft_id}`} />
          )}
          <div className="min-w-0">
            <div className="font-semibold text-zinc-900 truncate">{c.fullName || d.customer_name || d.customer_id}</div>
            <div className="text-xs text-zinc-500">{c.phoneNumber} · drafted {when(d.created_at)} by {d.created_by_key_name || "AI"}</div>
          </div>
        </div>
        <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded shrink-0 ${STATUS_STYLE[d.status] || ""}`} data-testid={`ai-draft-status-${d.draft_id}`}>
          {d.status === "scheduled" ? "held" : d.status}
        </span>
      </div>
      {c.opted_out && (
        <div className="text-xs font-semibold text-red-700 flex items-center gap-1" data-testid={`ai-draft-optout-${d.draft_id}`}>
          <ShieldAlert className="w-4 h-4" /> Opted out of texts. This draft can't be sent.
        </div>
      )}
      {c.last_inbound_message && (
        <div className="text-xs bg-zinc-50 rounded-lg p-2 text-zinc-600 flex gap-1.5">
          <MessageCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          <span><span className="font-semibold">Their last reply ({when(c.last_inbound_at)}):</span> {c.last_inbound_message}</span>
        </div>
      )}
      {d.note && <div className="text-xs text-zinc-500 italic">Why: {d.note}</div>}
      {pending ? (
        <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={3} maxLength={640}
          className="w-full rounded-lg border-2 border-zinc-200 p-2 text-sm focus:border-[#207024] focus:outline-none"
          data-testid={`ai-draft-body-${d.draft_id}`} />
      ) : (
        <div className="text-sm text-zinc-800 whitespace-pre-wrap bg-zinc-50 rounded-lg p-2">{d.body}</div>
      )}
      {d.edited && <div className="text-[11px] text-zinc-400">Edited before sending</div>}
      {(d.error || d.hold_reason) && d.status !== "pending" && (
        <div className="text-xs text-zinc-600 flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {d.error || d.hold_reason}{d.status === "scheduled" && d.scheduled_send_at ? ` (sends ${when(d.scheduled_send_at)})` : ""}</div>
      )}
      {d.status === "scheduled" && (
        <button onClick={() => onReject(d)} disabled={busy} data-testid={`ai-draft-cancel-${d.draft_id}`}
          className="h-10 w-full rounded-lg border-2 border-zinc-200 text-zinc-700 font-semibold text-sm flex items-center justify-center gap-1">
          <X className="w-4 h-4" /> Cancel this text
        </button>
      )}
      {d.delivery_status && <div className="text-xs text-zinc-500">Delivery: <span className="font-semibold">{d.delivery_status}</span>{d.approved_by ? ` · approved by ${d.approved_by}` : ""}</div>}
      {pending && (
        <div className="flex gap-2">
          <button onClick={() => onApprove(d, body)} disabled={busy || c.opted_out || !body.trim()}
            className="flex-1 h-11 rounded-lg bg-[#207024] text-white font-semibold text-sm flex items-center justify-center gap-1 disabled:opacity-50"
            data-testid={`ai-draft-approve-${d.draft_id}`}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Approve &amp; Send
          </button>
          <button onClick={() => onReject(d)} disabled={busy}
            className="h-11 px-4 rounded-lg border-2 border-zinc-200 text-zinc-700 font-semibold text-sm flex items-center gap-1"
            data-testid={`ai-draft-reject-${d.draft_id}`}>
            <X className="w-4 h-4" /> Reject
          </button>
        </div>
      )}
    </div>
  );
}

export default function AITextApprovals() {
  const [tab, setTab] = useState("pending");
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = () => axios.get(`${API}/admin/ai-sms/drafts?status=${tab}`, auth()).then((r) => setData(r.data))
    .catch(() => { toast.error("Couldn't load AI texts"); setData({ data: [], counts: {} }); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setData(null); setSelected([]); load(); }, [tab]);

  const report = (d) => {
    if (d.status === "sent") toast.success("Text sent");
    else if (d.status === "scheduled") toast.info(d.note || "Held until 8 AM Pacific");
    else toast.error(d.error || `Not sent (${d.status})`);
  };

  const approve = async (d, body) => {
    setBusy(d.draft_id);
    try {
      const r = await axios.post(`${API}/admin/ai-sms/drafts/${d.draft_id}/approve`, { body }, auth());
      report(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't approve"); }
    setBusy(null); load();
  };
  const reject = async (d) => {
    const reason = window.prompt("Reason for rejecting (optional, the assistant can read this):", "") ;
    if (reason === null) return;
    setBusy(d.draft_id);
    try { await axios.post(`${API}/admin/ai-sms/drafts/${d.draft_id}/reject`, { reason }, auth()); toast.success("Rejected"); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't reject"); }
    setBusy(null); load();
  };
  const bulk = async () => {
    if (!window.confirm(`Approve and send ${selected.length} text${selected.length === 1 ? "" : "s"} exactly as the AI wrote them? (Edits in the boxes are not used for bulk approve.)`)) return;
    setBusy("bulk");
    try {
      const r = await axios.post(`${API}/admin/ai-sms/drafts/bulk-approve`, { draft_ids: selected }, auth());
      const sent = r.data.results.filter((x) => x.status === "sent").length;
      const held = r.data.results.filter((x) => x.status === "scheduled").length;
      toast.success(`${sent} sent${held ? `, ${held} held for 8 AM` : ""}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Bulk approve failed"); }
    setBusy(null); setSelected([]); load();
  };
  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  return (
    <div className="min-h-screen bg-zinc-50 pb-20">
      <Header />
      <div className="max-w-2xl mx-auto px-4 py-5 space-y-4">
        <div>
          <h1 className="text-xl font-bold text-zinc-900 flex items-center gap-2"><Bot className="w-6 h-6 text-[#207024]" /> AI Text Approvals</h1>
          <p className="text-xs text-zinc-500 mt-1">Texts your AI assistant wants to send. Nothing goes out until you approve it.</p>
        </div>
        {data?.counts && (
          <div className="flex flex-wrap gap-2 text-xs" data-testid="ai-texts-counters">
            <span className="px-2 py-1 rounded bg-white border">AI texts sent today: <b>{data.counts.ai_sent_today}</b> / {data.counts.daily_cap}</span>
            {data.quiet_hours_now && <span className="px-2 py-1 rounded bg-blue-50 border border-blue-200 text-blue-800">Quiet hours: approved texts send at 8 AM</span>}
          </div>
        )}
        <div className="flex gap-1 bg-white border rounded-xl p-1" data-testid="ai-texts-tabs">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`ai-texts-tab-${t.id.split(",")[0]}`}
              className={`flex-1 h-9 rounded-lg text-xs font-semibold ${tab === t.id ? "bg-[#207024] text-white" : "text-zinc-600"}`}>
              {t.label}{t.id === "pending" && data?.counts?.pending ? ` (${data.counts.pending})` : ""}
            </button>
          ))}
        </div>
        {tab === "pending" && selected.length > 0 && (
          <button onClick={bulk} disabled={busy === "bulk"} data-testid="ai-texts-bulk-approve"
            className="w-full h-11 rounded-lg bg-zinc-900 text-white text-sm font-semibold flex items-center justify-center gap-1">
            {busy === "bulk" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Approve &amp; send {selected.length} selected
          </button>
        )}
        {!data ? <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[#207024]" /></div>
          : data.data.length === 0 ? <div className="text-center text-sm text-zinc-400 py-10" data-testid="ai-texts-empty">Nothing here</div>
            : data.data.map((d) => (
              <DraftCard key={d.draft_id} d={d} selected={selected.includes(d.draft_id)} onSelect={toggle}
                onApprove={approve} onReject={reject} busy={busy === d.draft_id} />
            ))}
      </div>
    </div>
  );
}
